'use strict';
const themeAttributeUtils = require('./theme-attribute-utils'),
	AUTO_COLOR = 'theme-auto-color',
	INHERIT_COLOR_MARKER = 'theme_inherit',
	calcChildPosition = require('./calc-child-position');
module.exports = function ThemeV2(themeDictionary) {
	//console.log(JSON.stringify(themeDictionary, null, 2));
	const self = this,
		connectorTypeDefault = themeDictionary.node.default.connections.style || 'default',
		defaultConnector = themeDictionary.connector[connectorTypeDefault],
		connectorDefaults = {type: defaultConnector.type, label: defaultConnector.label, line: defaultConnector.line},
		attributeValue = (prefixes, styles, postfixes, fallback) => {
			return themeAttributeUtils.themeAttributeValue(themeDictionary, prefixes, styles, postfixes, fallback);
		},
		getAutoColor = numberOfSiblings => {
			const fallbackColor = [AUTO_COLOR, INHERIT_COLOR_MARKER].includes(defaultConnector.line.color) ? '#707070' : defaultConnector.line.color,
				autoColors = themeDictionary.autoColors || [fallbackColor],
				index = (numberOfSiblings % autoColors.length);
			return autoColors[index];
		},
		themeConnectorArrows = (connectorAttr, childStyles) => {
			const childConnectorStyle = attributeValue(['node'], childStyles || [], ['connections', 'style']),
				childConnector = themeAttributeUtils.attributeForPath(themeDictionary, ['connector', childConnectorStyle]);
			return (connectorAttr && connectorAttr.arrow) || (childConnector && childConnector.line && childConnector.line.arrow);
		};

	self.name = themeDictionary && themeDictionary.name;
	self.connectorEditingContext = themeDictionary && themeDictionary.connectorEditingContext;
	self.attributeValue = (prefixes, styles, postfixes, fallback) => attributeValue(prefixes, styles, postfixes, fallback);

	self.nodeStyles = function (nodeLevel, nodeAttr) {
		const result = ['level_' + nodeLevel, 'default'];
		if (nodeAttr && nodeAttr.group) {
			result.unshift('attr_group');
			if (typeof nodeAttr.group === 'string' || typeof nodeAttr.group === 'number') {
				result.unshift('attr_group_' + nodeAttr.group);
			}
		}
		if (nodeAttr && nodeAttr.styleNames && Array.isArray(nodeAttr.styleNames)) {
			return nodeAttr.styleNames.concat(result);
		}
		return result;
	};

	self.nodeTheme = function (styles) {
		const nodeAttribute = attributeValue(['node'], (styles || []).concat(['default'])),
			computedTheme = themeAttributeUtils.nodeAttributeToNodeTheme(nodeAttribute);
		return computedTheme;
	};

	self.getFontForStyles = themeStyles => {
		const weight = attributeValue(['node'], themeStyles, ['text', 'font', 'weight']),
			size = attributeValue(['node'], themeStyles, ['text', 'font', 'size']),
			lineGap = attributeValue(['node'], themeStyles, ['text', 'font', 'lineSpacing']);
		return {size, weight, lineGap};
	};

	self.getNodeMargin = themeStyles => {
		if (!themeStyles || !Array.isArray(themeStyles)) {
			throw new Error('invalid-args');
		}

		return attributeValue(['node'], themeStyles, ['text', 'margin']);
	};
	self.canvasTheme = () => Object.assign({}, themeDictionary.canvas);

	self.linkTheme = (linkStyle) => {
		const fromCurrentTheme = themeAttributeUtils.attributeForPath(themeDictionary, ['link', linkStyle || 'default']),
			fromDefaultTheme = themeDictionary.link.default,
			computedTheme = Object.assign({}, fromDefaultTheme, fromCurrentTheme);
		if (computedTheme.label && typeof computedTheme.label === 'object') {
			computedTheme.label = Object.assign({}, computedTheme.label);
		}
		return computedTheme;
	};

	self.noAnimations = () => !!(themeDictionary.noAnimations);

	self.getLayoutConnectorAttributes = (styles) => {
		const childConnectorStyle = attributeValue(['node'], styles, ['connections', 'style']),
			childConnector = themeAttributeUtils.attributeForPath(themeDictionary, ['connector', childConnectorStyle]) || Object.assign({}, connectorDefaults),
			result = {};
		if (childConnector && childConnector.line) {
			result.parentConnector = {
				color: childConnector.line.color
			};
		}
		return result;
	};
	self.cleanPersistedAttributes = (currentAttribs) => {
		if (currentAttribs && currentAttribs.parentConnector && currentAttribs.parentConnector.themeAutoColor) {
			if (currentAttribs.parentConnector.themeAutoColor === currentAttribs.parentConnector.color) {
				delete currentAttribs.parentConnector.color;
			}
			delete currentAttribs.parentConnector.themeAutoColor;
			if (!currentAttribs || !currentAttribs.parentConnector || !Object.keys(currentAttribs.parentConnector).length) {
				delete currentAttribs.parentConnector;
			}
		}
		return currentAttribs;
	};
	self.getPersistedAttributes = (currentAttribs, nodeLevel, numberOfSiblings) => {
		const styles = ['level_' + nodeLevel, 'default'],
			childConnectorStyle = attributeValue(['node'], styles, ['connections', 'style']),
			childConnector = themeAttributeUtils.attributeForPath(themeDictionary, ['connector', childConnectorStyle]) || Object.assign({}, connectorDefaults),
			autoColor = getAutoColor(numberOfSiblings),
			result = {
				attr: (currentAttribs && currentAttribs.parentConnector && {parentConnector: currentAttribs.parentConnector}) || {},
				removed: []
			};
		if (childConnector && childConnector.line && childConnector.line.color === AUTO_COLOR) {
			result.attr.parentConnector = Object.assign({
				color: autoColor,
				themeAutoColor: autoColor
			}, result.attr.parentConnector);
		} else if (result.attr.parentConnector && result.attr.parentConnector.themeAutoColor) {
			result.attr.parentConnector = Object.assign({}, result.attr.parentConnector);
			if (result.attr.parentConnector.themeAutoColor === result.attr.parentConnector.color) {
				delete result.attr.parentConnector.color;
			}
			delete result.attr.parentConnector.themeAutoColor;

			if (!result || !result.attr || !result.attr.parentConnector || !Object.keys(result.attr.parentConnector).length) {
				result.removed.push('parentConnector');
				delete result.attr.parentConnector;
			}
		}
		return result;
	};
	self.connectorTheme = ({parent, child, connectorAttr}) => {
		const childPosition = calcChildPosition(parent, child, 10),
			fromStyles = parent.styles || ['default'],
			toStyles = child.styles || ['default'],
			connectorArrows = themeConnectorArrows(connectorAttr, toStyles),
			connectionTypeFrom = attributeValue(['node'], fromStyles, ['connections', 'style']),
			connectionTypeTo = attributeValue(['node'], toStyles, ['connections', 'style']),
			connectionPositionDefaultFrom = attributeValue(['node'], fromStyles, ['connections', connectionTypeFrom], {h: 'center', v: 'center'}),
			connectionPositionDefaultTo = attributeValue(['node'], toStyles, ['connections', connectionTypeTo], {h: 'nearest-inset', v: 'center'}),
			fromConnectionsAttribute = ['from', 'both'].includes(connectorArrows) ? 'from-arrow' : 'from',
			toConnectionsAttribute = ['to', 'both'].includes(connectorArrows) ? 'to-arrow' : 'to',

			connectionPositionFrom = Object.assign({}, connectionPositionDefaultFrom, attributeValue(['node'], fromStyles, ['connections', fromConnectionsAttribute, childPosition], {})),

			connectionPositionTo = Object.assign({}, connectionPositionDefaultTo, attributeValue(['node'], toStyles, ['connections', toConnectionsAttribute], {})),

			fromInset = attributeValue(['node'], fromStyles, ['cornerRadius'], 10),
			toInset = attributeValue(['node'], toStyles, ['cornerRadius'], 10),
			borderType = attributeValue(['node'], toStyles, ['border', 'type'], ''),

			position = childPosition || 'horizontal',
			childConnectorStyle = attributeValue(['node'], toStyles || [], ['connections', 'style']),
			parentConnectorStyle = fromStyles && attributeValue(['node'], fromStyles, ['connections', 'childstyle']),
			childConnector = themeAttributeUtils.attributeForPath(themeDictionary, ['connector', childConnectorStyle]),
			parentConnector = parentConnectorStyle && themeAttributeUtils.attributeForPath(themeDictionary, ['connector', parentConnectorStyle]),
			combinedStyle = parentConnectorStyle && (parentConnectorStyle + '.' + childConnectorStyle),
			combinedConnector = combinedStyle &&  themeAttributeUtils.attributeForPath(themeDictionary, ['connector', combinedStyle]),
			connectorStyle  = (combinedConnector && combinedStyle) || (parentConnector && parentConnectorStyle) || childConnectorStyle || 'default',
			controlPoint = themeAttributeUtils.connectorControlPoint(themeDictionary, position, connectorStyle, connectorArrows),
			result =  Object.assign({}, combinedConnector || parentConnector || childConnector || connectorDefaults);

		if (!result.label) {
			result.label = connectorDefaults.label;
		}
		result.controlPoint = controlPoint;
		result.line = Object.assign({}, result.line ||  connectorDefaults.line);
		result.line.lineStyle = result.line.lineStyle || 'solid';
		if (result.label && typeof result.label === 'object') {
			result.label = Object.assign({}, result.label);
		}
		result.line.arrowExtension = {length: 4, width: 9};

		Object.assign(result.line, connectorAttr);
		return Object.assign(result, {connectionPositionFrom, connectionPositionTo, fromInset, toInset, borderType});
	};

};
